import type * as monacoTypes from 'monaco-editor';

export const moveFocus = (forward: boolean) => {
  const selector = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  const all = Array.from(document.querySelectorAll<HTMLElement>(selector))
    // only visible-ish
    .filter(el => el.offsetParent !== null || getComputedStyle(el).position === 'fixed');

  const active = document.activeElement as HTMLElement | null;
  const idx = active ? all.indexOf(active) : -1;

  const nextIdx = (() => {
    if (idx === -1) return 0;
    if (forward) return (idx + 1) % all.length;
    return (idx - 1 + all.length) % all.length;
  })();

  all[nextIdx]?.focus();
};

export const overrideMonacoKeybindings = (monaco: typeof monacoTypes) => {
  // Clear out almost all key bindings.
  //
  // This doesn't clear out some complex key bindings, but there aren't many of
  // these by default and in testing I wasn't able to trigger them intentionally.

  const keepKeybindings = [
    // Undo
    monaco.KeyCode.KeyZ | monaco.KeyMod.CtrlCmd,
    // Redo
    monaco.KeyCode.KeyZ | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift,
    // Toggle Line Comment (Cmd+/ or Ctrl+/)
    monaco.KeyCode.Slash | monaco.KeyMod.CtrlCmd,
    // Trigger Suggest (Ctrl+Space on both Mac and Windows)
    monaco.KeyCode.Space | monaco.KeyMod.CtrlCmd,
    monaco.KeyCode.Space | monaco.KeyMod.WinCtrl,
    // Select All (Cmd+A or Ctrl+A)
    monaco.KeyCode.KeyA | monaco.KeyMod.CtrlCmd,

    // --- Standard text navigation shortcuts (macOS emacs bindings) ---
    // Ctrl+A = line start, Ctrl+E = line end
    monaco.KeyCode.KeyA | monaco.KeyMod.WinCtrl,
    monaco.KeyCode.KeyE | monaco.KeyMod.WinCtrl,
    // Ctrl+Shift+A / Ctrl+Shift+E = select to line start/end
    monaco.KeyCode.KeyA | monaco.KeyMod.WinCtrl | monaco.KeyMod.Shift,
    monaco.KeyCode.KeyE | monaco.KeyMod.WinCtrl | monaco.KeyMod.Shift,

    // --- Word navigation (Option+Arrow on macOS, Ctrl+Arrow on Windows) ---
    monaco.KeyCode.LeftArrow | monaco.KeyMod.Alt,
    monaco.KeyCode.RightArrow | monaco.KeyMod.Alt,
    // Word selection (Option+Shift+Arrow)
    monaco.KeyCode.LeftArrow | monaco.KeyMod.Alt | monaco.KeyMod.Shift,
    monaco.KeyCode.RightArrow | monaco.KeyMod.Alt | monaco.KeyMod.Shift,

    // --- Line/document navigation (Cmd+Arrow on macOS, Home/End on Windows) ---
    monaco.KeyCode.LeftArrow | monaco.KeyMod.CtrlCmd,
    monaco.KeyCode.RightArrow | monaco.KeyMod.CtrlCmd,
    monaco.KeyCode.UpArrow | monaco.KeyMod.CtrlCmd,
    monaco.KeyCode.DownArrow | monaco.KeyMod.CtrlCmd,
    // Line/document selection (Cmd+Shift+Arrow)
    monaco.KeyCode.LeftArrow | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift,
    monaco.KeyCode.RightArrow | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift,
    monaco.KeyCode.UpArrow | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift,
    monaco.KeyCode.DownArrow | monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift,

    // --- Delete by word/line ---
    // Option+Backspace = delete word left, Cmd+Backspace = delete to line start
    monaco.KeyCode.Backspace | monaco.KeyMod.Alt,
    monaco.KeyCode.Backspace | monaco.KeyMod.CtrlCmd,
  ];

  const modifiers = [monaco.KeyMod.CtrlCmd, monaco.KeyMod.Shift, monaco.KeyMod.Alt, monaco.KeyMod.WinCtrl];

  // Generate all possible keybindings formed by a combination of a single key and one or more modifier keys.
  //
  // Start at 1 for the key code since monaco.KeyCode.Unknown is 0
  for (let key = 1; key < monaco.KeyCode.MAX_VALUE; key++) {
    // Iterate through all non-empty subsets of the modifiers.
    for (let modifierSubset = 1; modifierSubset < 1 << modifiers.length; modifierSubset++) {
      // Build up the keybinding represented by the `key` and `modifierSubset`.
      let keybinding = key;
      for (let i = 0; i < modifiers.length; i++) {
        if (modifierSubset & (1 << i)) {
          keybinding |= modifiers[i];
        }
      }

      if (!keepKeybindings.includes(keybinding)) {
        monaco.editor.addKeybindingRule({
          keybinding,
          command: null,
        });
      }
    }
  }
};
